// Image helpers for /start: colour maths, logo normalisation, the initials logo
// used when there is no upload, colours taken from a logo, and stamp icons as
// PNG. Phone photos are large (24 MP and up) and iOS caps a canvas at ~16.7 MP,
// so nothing here ever draws a full-resolution image onto a canvas.

export const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export function hexToRgb(hex) {
  const value = String(hex || "").replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(value)) return null;
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }) {
  const part = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}

function channel(c) {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

export function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export const LIGHT_TEXT = "#FFFFFF";
export const DARK_TEXT = "#111827";

// Text colour for a card or form background: whichever of white or near-black
// reads better (WCAG contrast).
export function readableTextColor(background) {
  return contrastRatio(background, LIGHT_TEXT) >= contrastRatio(background, DARK_TEXT)
    ? LIGHT_TEXT
    : DARK_TEXT;
}

// First letters of the first two words, or the first two characters of a single
// word ("Corner Cuts" → "CC", "Lola" → "LO"). Grapheme-aware and uppercased for
// the given language, so Turkish "istanbul" becomes "İS".
export function initialsFor(name, lang) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const graphemes = (text) => {
    if (typeof Intl !== "undefined" && Intl.Segmenter) {
      return Array.from(new Intl.Segmenter(lang, { granularity: "grapheme" }).segment(text), (s) => s.segment);
    }
    return Array.from(text);
  };
  const letters =
    words.length >= 2
      ? [graphemes(words[0])[0], graphemes(words[1])[0]]
      : graphemes(words[0]).slice(0, 2);
  const joined = letters.join("");
  try {
    return joined.toLocaleUpperCase(lang || undefined);
  } catch {
    return joined.toUpperCase();
  }
}

function canvasToBlob(canvas, type = "image/png", quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not export image"))),
      type,
      quality,
    );
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Could not read image"));
    reader.readAsDataURL(blob);
  });
}

export function dataUrlToBlob(dataUrl) {
  const [header, body] = String(dataUrl).split(",");
  const mime = (header.match(/data:([^;]+)/) || [])[1] || "application/octet-stream";
  const isBase64 = /;base64/.test(header);
  const raw = isBase64 ? atob(body) : decodeURIComponent(body);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function releaseCanvas(canvas) {
  // Frees the backing store right away; iOS counts canvas memory per page.
  canvas.width = 0;
  canvas.height = 0;
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not decode image"));
    img.src = src;
  });
}

// Decodes a picked file. createImageBitmap can scale while decoding, which keeps
// big photos off the canvas size limit; older WebKit falls back to <img>.
async function decodeImage(file, maxSide) {
  if (typeof createImageBitmap === "function") {
    try {
      const probe = await createImageBitmap(file);
      const scale = Math.min(1, maxSide / Math.max(probe.width, probe.height));
      if (scale >= 1) return { source: probe, width: probe.width, height: probe.height };
      const width = Math.max(1, Math.round(probe.width * scale));
      const height = Math.max(1, Math.round(probe.height * scale));
      probe.close?.();
      const resized = await createImageBitmap(file, { resizeWidth: width, resizeHeight: height, resizeQuality: "high" });
      return { source: resized, width, height };
    } catch {
      // fall through to <img>
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImageElement(url);
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const LOGO_SIZE = 512;
export const MAX_LOGO_BYTES = 5 * 1024 * 1024;

// Any picked image → a 512×512 PNG with the logo centred and fitted (never
// cropped), transparent around it. The backend accepts PNG/JPEG under 5 MB.
export async function normalizeLogo(file) {
  const { source, width, height } = await decodeImage(file, 1024);
  const canvas = document.createElement("canvas");
  canvas.width = LOGO_SIZE;
  canvas.height = LOGO_SIZE;
  const ctx = canvas.getContext("2d");
  const scale = Math.min(LOGO_SIZE / width, LOGO_SIZE / height);
  const w = width * scale;
  const h = height * scale;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, (LOGO_SIZE - w) / 2, (LOGO_SIZE - h) / 2, w, h);
  source.close?.();
  const blob = await canvasToBlob(canvas, "image/png");
  const color = paletteFromCanvas(canvas);
  releaseCanvas(canvas);
  if (blob.size > MAX_LOGO_BYTES) throw new Error("Logo too large");
  return { blob, dataUrl: await blobToDataUrl(blob), color };
}

// Most common clearly-coloured pixel of the logo, as a candidate card colour.
// Near-white and transparent pixels are the logo's background, not its colour.
function paletteFromCanvas(canvas) {
  try {
    const sample = document.createElement("canvas");
    sample.width = 48;
    sample.height = 48;
    const ctx = sample.getContext("2d");
    ctx.drawImage(canvas, 0, 0, 48, 48);
    const { data } = ctx.getImageData(0, 0, 48, 48);
    releaseCanvas(sample);
    const counts = new Map();
    for (let i = 0; i < data.length; i += 4) {
      const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]];
      if (a < 160) continue;
      if (r > 232 && g > 232 && b > 232) continue;
      const key = `${r >> 4},${g >> 4},${b >> 4}`;
      const entry = counts.get(key) || { n: 0, r: 0, g: 0, b: 0 };
      entry.n += 1;
      entry.r += r;
      entry.g += g;
      entry.b += b;
      counts.set(key, entry);
    }
    let best = null;
    for (const entry of counts.values()) {
      if (!best || entry.n > best.n) best = entry;
    }
    if (!best || best.n < 12) return null;
    return rgbToHex({ r: best.r / best.n, g: best.g / best.n, b: best.b / best.n });
  } catch {
    return null;
  }
}

// Wallet passes need a logo (Apple's icon.png comes from it), so a store with no
// upload gets its initials: a circle in the card's text colour with the
// initials in the card colour.
export async function makeMonogramLogo(name, { background, foreground, lang } = {}) {
  const text = initialsFor(name, lang) || "★";
  try {
    await Promise.race([
      document.fonts?.load?.("700 200px InterVariable"),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  } catch {
    // system font is fine
  }
  const canvas = document.createElement("canvas");
  canvas.width = LOGO_SIZE;
  canvas.height = LOGO_SIZE;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = foreground || LIGHT_TEXT;
  ctx.beginPath();
  ctx.arc(LOGO_SIZE / 2, LOGO_SIZE / 2, LOGO_SIZE / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = background || "#18122B";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const fontSize = text.length > 1 ? 220 : 260;
  ctx.font = `700 ${fontSize}px InterVariable, Inter, -apple-system, system-ui, sans-serif`;
  ctx.fillText(text, LOGO_SIZE / 2, LOGO_SIZE / 2 + fontSize * 0.04);
  const blob = await canvasToBlob(canvas, "image/png");
  releaseCanvas(canvas);
  return { blob, dataUrl: await blobToDataUrl(blob) };
}

// Stamp icon as a PNG (the dashboard re-draws stamps from the uploaded
// stamp_icon, and html2canvas renders PNG data URLs reliably).
export async function stampIconPng(svgDataUrl, size = 256) {
  const img = await loadImageElement(svgDataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  canvas.getContext("2d").drawImage(img, 0, 0, size, size);
  const blob = await canvasToBlob(canvas, "image/png");
  const dataUrl = canvas.toDataURL("image/png");
  releaseCanvas(canvas);
  return { blob, dataUrl };
}

export async function solidStripPng(color, width = 1140, height = 435) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);
  const blob = await canvasToBlob(canvas, "image/png");
  releaseCanvas(canvas);
  return blob;
}

export { blobToDataUrl };

// Logos come from the CDN; drawing a cross-origin image would taint the canvas
// (or be silently dropped), so it is fetched with CORS into a local blob URL.
export async function localImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const response = await fetch(url, { mode: "cors", cache: "no-store" });
    if (!response.ok) return null;
    return URL.createObjectURL(await response.blob());
  } catch {
    return null;
  }
}
